import { FC, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DefaultLayout from '@/layouts/Default/default';
import { Seo } from '@/components/SEO/Seo';
import { workProjects } from '@/components/Work/StackedCarousel/workProjects';
import type { WorkProject } from '@/components/Work/StackedCarousel/types';
import SummarySection from '@/components/Work/StackedCarousel/components/ExpandedBlocks/SummarySection';
import HeroGallerySection from '@/components/Work/StackedCarousel/components/ExpandedBlocks/HeroGallerySection';
import ScopeSection from '@/components/Work/StackedCarousel/components/ExpandedBlocks/ScopeSection';
import CaseDetailsSection from '@/components/Work/StackedCarousel/components/ExpandedBlocks/CaseDetailsSection';
import CaseStudyContentSection from '@/components/Work/StackedCarousel/components/ExpandedBlocks/CaseStudyContentSection';
import PrototypeGallerySection from '@/components/Work/StackedCarousel/components/ExpandedBlocks/PrototypeGallerySection';
import NextCaseSection from '@/components/Work/StackedCarousel/components/ExpandedBlocks/NextCaseSection';
import { useExpandedViewContent } from '@/components/Work/StackedCarousel/components/useExpandedViewContent';
import type { WorkCaseMediaSource } from '@/components/Work/StackedCarousel/types';
import { siteMetadata } from '@/config/seo';

const getProjectBySlug = (slug: string): { project: WorkProject; index: number } => {
  if (!workProjects.length) {
    throw new Error('No work projects available');
  }

  const normalized = slug.trim().toLowerCase();
  const index = workProjects.findIndex((project) => {
    const identifiers = [project.caseStudySlug, project.id].filter(Boolean) as string[];
    return identifiers.some((identifier) => identifier.toLowerCase() === normalized);
  });

  if (index === -1) {
    return { project: workProjects[0], index: 0 };
  }

  return { project: workProjects[index], index };
};

const getHeroSources = (project: WorkProject): WorkCaseMediaSource[] => {
  if (project.mediaType === 'video' && project.mediaSources?.length) {
    return project.mediaSources;
  }

  if (project.mediaType === 'video') {
    const inferredType = project.media.endsWith('.mov') ? 'video/quicktime' : 'video/mp4';
    return [{ src: project.media, type: inferredType }];
  }

  return [];
};

const buildAbsoluteUrl = (input?: string): string | undefined => {
  if (!input) return undefined;
  if (/^https?:\/\//i.test(input)) return input;
  const normalized = input.startsWith('/') ? input : `/${input}`;
  return `${siteMetadata.siteUrl}${normalized}`;
};

const WorkCaseStudyPage: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const slug = useMemo(() => location.pathname.replace(/^\/work\//, '').replace(/\/$/, ''), [location.pathname]);

  const { project, index } = useMemo(() => getProjectBySlug(slug), [slug]);
  const totalProjects = workProjects.length || 1;
  const nextProject = workProjects[(index + 1) % totalProjects] ?? project;

  const [detailsExpanded, setDetailsExpanded] = useState(true);
  const { detailRows, hasDetails, heroGalleryMedia, gallerySets, caseStudy } = useExpandedViewContent(project);

  const heroSources = getHeroSources(project);
  const heroPrimaryVideoSource = heroSources[0]?.src ?? project.media;
  const [heroVideoLoaded, setHeroVideoLoaded] = useState(project.mediaType !== 'video');
  useEffect(() => {
    setHeroVideoLoaded(project.mediaType !== 'video');
  }, [project.id, project.mediaType]);
  const Logo = project.logo;
  const heroPlaceholderStyle = project.placeholder
    ? {
        backgroundImage: `url(${project.placeholder})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : undefined;
  const heroSummary = project.headline ?? project.description ?? project.summary[0] ?? project.quote;
  const canonicalPath = useMemo(() => (slug ? `/work/${slug}` : '/work'), [slug]);
  const socialImage = project.mediaType === 'image' ? project.media : project.placeholder ?? project.poster ?? undefined;
  const structuredData = useMemo(() => {
    const descriptionText = heroSummary ?? project.description ?? siteMetadata.defaultDescription;
    const caseStudySchema = {
      '@context': 'https://schema.org',
      '@type': 'CaseStudy',
      name: project.title,
      headline: project.title,
      description: descriptionText,
      url: `${siteMetadata.siteUrl}${canonicalPath}`,
      genre: project.category,
      keywords: project.tags?.length ? project.tags.join(', ') : undefined,
      image: buildAbsoluteUrl(socialImage ?? siteMetadata.defaultImage),
      inLanguage: 'en',
      provider: {
        '@type': 'Organization',
        name: siteMetadata.siteName,
        url: siteMetadata.siteUrl,
      },
    };
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Work',
          item: `${siteMetadata.siteUrl}/work`,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: project.title,
          item: `${siteMetadata.siteUrl}${canonicalPath}`,
        },
      ],
    };
    return [caseStudySchema, breadcrumbSchema];
  }, [canonicalPath, heroSummary, project.category, project.description, project.tags, project.title, socialImage]);

  const seo = useMemo(
    () => ({
      title: `${project.title} · Turbo Labs Work`,
      description: heroSummary ?? project.description ?? siteMetadata.defaultDescription,
      image: socialImage ?? siteMetadata.defaultImage,
      imageAlt: `${project.title} case study preview`,
      canonicalPath,
      type: 'article' as const,
      keywords: project.tags,
      structuredData,
    }),
    [canonicalPath, heroSummary, project.description, project.tags, project.title, socialImage, structuredData],
  );

  const handleAdvanceToNext = () => {
    if (!nextProject) return;
    const targetSlug = nextProject.caseStudySlug ?? nextProject.id;
    if (!targetSlug) return;
    navigate(`/work/${targetSlug}`, { replace: true });
  };

  return (
    <DefaultLayout hideHeader hideFooter>
      <Seo {...seo} />
      <article className="min-h-screen bg-white text-zinc-900 transition-colors dark:bg-neutral-950 dark:text-white">
        <header className="relative isolate w-full overflow-hidden rounded-b-[36px] bg-black">
          <div className="grid h-[40vh] min-h-[240px] w-full grid-cols-1 grid-rows-1">
            {project.placeholder ? (
              <div
                aria-hidden="true"
                className={`col-span-full row-span-full transition-opacity duration-700 ease-out ${
                  heroVideoLoaded ? 'opacity-0' : 'opacity-100'
                }`}
                style={{
                  ...heroPlaceholderStyle,
                  filter: 'blur(32px) brightness(0.4)',
                  transform: 'scale(1.05)',
                }}
              />
            ) : null}

            {project.mediaType === 'video' ? (
              <video
                className="col-span-full row-span-full h-full w-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                poster={project.poster ?? project.placeholder}
                src={heroPrimaryVideoSource}
                onLoadedData={() => setHeroVideoLoaded(true)}
                onCanPlay={() => setHeroVideoLoaded(true)}
              >
                {heroSources.map(({ src, type }) => (
                  <source key={`${src}-${type ?? 'video'}`} src={src} type={type} />
                ))}
              </video>
            ) : (
              <img
                src={project.media}
                alt={project.title}
                className="col-span-full row-span-full h-full w-full object-cover"
                loading="lazy"
              />
            )}

            <div
              aria-hidden="true"
              className="col-span-full row-span-full bg-gradient-to-b from-black/80 via-black/85 to-black/95"
            />

            <div className="col-span-full row-span-full z-[1] flex items-center justify-center px-6 text-center">
              {Logo ? (
                <Logo aria-label={`${project.title} logo`} className="h-14 w-auto text-white sm:h-16" role="img" />
              ) : (
                <span className="font-konexy text-[clamp(2.75rem,5vw,4rem)] lowercase text-white">
                  {project.title.toLowerCase()}
                </span>
              )}
            </div>
          </div>
        </header>

        <div className="w-full space-y-16 px-4 pb-16 pt-10 sm:px-10">
          <div className="-mx-4 sm:-mx-8">
            <SummarySection project={project} />
            <HeroGallerySection media={heroGalleryMedia} />
          </div>

          {project.scope.length > 0 && (
            <div className="-mx-4 sm:-mx-8">
              <ScopeSection project={project} />
            </div>
          )}

          {hasDetails && (
            <div className="-mx-4 sm:-mx-8">
              <CaseDetailsSection
                detailRows={detailRows}
                hasDetails={hasDetails}
                detailsExpanded={detailsExpanded}
                onToggle={() => setDetailsExpanded((previous) => !previous)}
              />
            </div>
          )}

          {caseStudy && (
            <div className="-mx-4 sm:-mx-8">
              <CaseStudyContentSection caseStudy={caseStudy} />
            </div>
          )}

          {gallerySets.length > 0 && (
            <div className="-mx-4 sm:-mx-8">
              <PrototypeGallerySection galleries={gallerySets} />
            </div>
          )}

          <div className="-mx-4 sm:-mx-8">
            <NextCaseSection nextProject={nextProject} onAdvance={handleAdvanceToNext} />
          </div>
        </div>
      </article>
    </DefaultLayout>
  );
};

export default WorkCaseStudyPage;
